// ═══════════════════════════════════════════════════════════════
// HOOK: useSocialSecurityConfig - CON SOPORTE GLOBAL
// ✅ globalConfig en state (persiste en localStorage)
// ✅ getEffectiveConfig: individual > global
// ✅ saveGlobalConfig: elimina configs individuales para forzar herencia
// ✅ calculateSS acepta employeeId y resuelve config internamente
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useCompany } from '../contexts/CompanyContext';
import { payrollConfigAPI } from '../api/payrollConfig';

// ─────────────────────────────────────────────────────────────
// Config global por defecto (tasas 2024-2025 RD)
// ─────────────────────────────────────────────────────────────
function getDefaultGlobalConfig() {
  return {
    descontar_global: true,
    incluir_incentivos_global: false,
    descontar_capita_global: true,
    porcentaje_afp_empleado: 0.0287,
    porcentaje_sfs_empleado: 0.0304,
    porcentaje_afp_empleador: 0.0710,
    porcentaje_sfs_empleador: 0.0709,
    porcentaje_srl_empleador: 0.0120,
    porcentaje_infotep: 0.0100
  };
}

export function useSocialSecurityConfig() {
  const { currentCompany } = useCompany();

  const [configs, setConfigs] = useState([]);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─────────────────────────────────────────────────────────
  // Cargar configs: tasas desde API, configs individuales desde localStorage
  // ─────────────────────────────────────────────────────────
  const loadConfigs = useCallback(async () => {
    if (!currentCompany) {
      setConfigs([]);
      setGlobalConfig(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Individuales (siguen en localStorage)
      const storedConfigs = localStorage.getItem(`ss_configs_${currentCompany.id}`);
      setConfigs(storedConfigs ? JSON.parse(storedConfigs) : []);

      // Global: intentar cargar desde API (tss_rates), fallback a localStorage/defaults
      try {
        const tssRates = await payrollConfigAPI.getTSSRates();
        if (tssRates && tssRates.length > 0) {
          const map = Object.fromEntries(tssRates.map(r => [r.concepto, r]));
          // Convertir porcentajes de DB (2.87) a decimales (0.0287)
          const toDecimal = (v) => {
            const n = parseFloat(v) || 0;
            return n >= 1 ? n / 100 : n;
          };
          const afp = map['AFP'] || {};
          const sfs = map['SFS'] || {};
          const srl = map['SRL'] || {};
          const inf = map['INFOTEP'] || {};
          const globalFromDB = {
            porcentaje_afp_empleado:  toDecimal(afp.porcentaje_empleado)  || 0.0287,
            porcentaje_sfs_empleado:  toDecimal(sfs.porcentaje_empleado)  || 0.0304,
            porcentaje_afp_empleador: toDecimal(afp.porcentaje_empleador) || 0.0710,
            porcentaje_sfs_empleador: toDecimal(sfs.porcentaje_empleador) || 0.0709,
            porcentaje_srl_empleador: toDecimal(srl.porcentaje_empleador) || 0.0120,
            porcentaje_infotep:       toDecimal(inf.porcentaje_empleador) || 0.0100,
            tope_afp: afp.tope_mensual != null ? parseFloat(afp.tope_mensual) : 464460,
            tope_sfs: sfs.tope_mensual != null ? parseFloat(sfs.tope_mensual) : 232230,
            tope_srl: srl.tope_mensual != null ? parseFloat(srl.tope_mensual) : 92892,
            // Flags per-rate desde tss_rates
            descontar_afp: afp.descontar_al_empleado != null ? Boolean(afp.descontar_al_empleado) : true,
            descontar_sfs: sfs.descontar_al_empleado != null ? Boolean(sfs.descontar_al_empleado) : true,
            incluir_cotizable_afp: Boolean(afp.incluir_en_cotizable ?? false),
            incluir_cotizable_sfs: Boolean(sfs.incluir_en_cotizable ?? false),
            incluir_cotizable_srl: Boolean(srl.incluir_en_cotizable ?? false),
            incluir_cotizable_inf: Boolean(inf.incluir_en_cotizable ?? false),
            // Opciones globales — se leen de localStorage si existen (son por empresa, no por tasa)
            ...(() => {
              try {
                const stored = localStorage.getItem(`ss_global_config_${currentCompany.id}`);
                const parsed = stored ? JSON.parse(stored) : {};
                return {
                  descontar_global:           parsed.descontar_global           ?? true,
                  incluir_incentivos_global:  parsed.incluir_incentivos_global  ?? false,
                  descontar_capita_global:    parsed.descontar_capita_global    ?? true
                };
              } catch { return { descontar_global: true, incluir_incentivos_global: false, descontar_capita_global: true }; }
            })()
          };
          setGlobalConfig(globalFromDB);
        } else {
          throw new Error('Sin tasas en BD');
        }
      } catch {
        // Fallback a localStorage
        const storedGlobal = localStorage.getItem(`ss_global_config_${currentCompany.id}`);
        if (storedGlobal) {
          setGlobalConfig(JSON.parse(storedGlobal));
        } else {
          const defaultGlobal = getDefaultGlobalConfig();
          localStorage.setItem(`ss_global_config_${currentCompany.id}`, JSON.stringify(defaultGlobal));
          setGlobalConfig(defaultGlobal);
        }
      }
    } catch (error) {
      console.error('Error loading SS configs:', error);
      setConfigs([]);
      setGlobalConfig(getDefaultGlobalConfig());
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // ─────────────────────────────────────────────────────────
  // Config individual raw (puede retornar null)
  // ─────────────────────────────────────────────────────────
  const getConfig = (employeeId) => {
    return configs.find(c => c.employeeId === employeeId) || null;
  };

  // ─────────────────────────────────────────────────────────
  // Config efectiva normalizada: individual si existe, sino global
  // ─────────────────────────────────────────────────────────
  const getEffectiveConfig = useCallback((employeeId) => {
    const individual = configs.find(c => c.employeeId === employeeId);

    if (individual) {
      return {
        incluirIncentivos:  individual.incluir_incentivos ?? false,
        capitaDependientes: individual.capita_dependientes || 0,
        descontarAlEmpleado: individual.descontar_empleado ?? true,
        descontarCapita:    individual.descontar_capita ?? true
      };
    }

    // Hereda de global — descontar = ambas tasas deben estar activas
    const g = globalConfig || getDefaultGlobalConfig();
    return {
      incluirIncentivos:  (g.incluir_cotizable_afp || g.incluir_cotizable_sfs) ?? g.incluir_incentivos_global ?? false,
      capitaDependientes: 0,                              // Cápita es siempre individual
      descontarAlEmpleado: (g.descontar_afp ?? g.descontar_global ?? true) && (g.descontar_sfs ?? g.descontar_global ?? true),
      descontarCapita:    g.descontar_capita_global ?? true
    };
  }, [configs, globalConfig]);

  // ─────────────────────────────────────────────────────────
  // Guardar config individual
  // ─────────────────────────────────────────────────────────
  const saveConfig = async (employeeId, configData) => {
    const newConfig = {
      employeeId,
      ...configData,
      companyId: currentCompany.id,
      updatedAt: new Date().toISOString()
    };

    const existingIndex = configs.findIndex(c => c.employeeId === employeeId);
    const updatedConfigs = existingIndex >= 0
      ? configs.map((c, i) => i === existingIndex ? newConfig : c)
      : [...configs, newConfig];

    setConfigs(updatedConfigs);
    localStorage.setItem(`ss_configs_${currentCompany.id}`, JSON.stringify(updatedConfigs));
    return newConfig;
  };

  // ─────────────────────────────────────────────────────────
  // Guardar config global
  // Elimina configs individuales para que todos hereden la global.
  // Sin este paso, getEffectiveConfig seguiría retornando las
  // configs individuales viejas y los cambios globales no se verían.
  // ─────────────────────────────────────────────────────────
  const saveGlobalConfig = async (newConfig) => {
    const updatedGlobal = {
      ...getDefaultGlobalConfig(),
      ...newConfig,
      updatedAt: new Date().toISOString()
    };

    // 1) Guardar global
    setGlobalConfig(updatedGlobal);
    localStorage.setItem(`ss_global_config_${currentCompany.id}`, JSON.stringify(updatedGlobal));

    // 2) Borrar todas las individuales
    console.log(`🗑️ Eliminando ${configs.length} configs individuales para aplicar global`);
    setConfigs([]);
    localStorage.removeItem(`ss_configs_${currentCompany.id}`);

    return updatedGlobal;
  };

  // ─────────────────────────────────────────────────────────
  // Calcular Seguridad Social
  // Acepta { salarioBase, comisiones, incentivos, employeeId }
  // Si pasa employeeId resuelve la config internamente.
  // ─────────────────────────────────────────────────────────
  const calculateSS = useCallback((data) => {
    const { salarioBase, comisiones = 0, incentivos = 0, employeeId } = data;

    const g = globalConfig || getDefaultGlobalConfig();

    // ── Resolver config individual si hay employeeId ──
    const individual = employeeId ? configs.find(c => c.employeeId === employeeId) : null;

    // ── Flags per-rate: descontar al empleado ──
    // Config individual (descontar_empleado) aplica a TODAS las tasas del empleado.
    // Sin config individual se usa el flag per-rate guardado en tss_rates (via globalConfig).
    const hasEmpDescontar = individual && individual.descontar_empleado != null;
    const deductAFP = hasEmpDescontar
      ? Boolean(individual.descontar_empleado)
      : (g.descontar_afp ?? true);
    const deductSFS = hasEmpDescontar
      ? Boolean(individual.descontar_empleado)
      : (g.descontar_sfs ?? true);

    // ── Flags per-rate: incluir incentivos en cotizable ──
    const hasEmpIncInc = individual && individual.incluir_incentivos != null;
    const inclIncAFP = hasEmpIncInc ? Boolean(individual.incluir_incentivos) : (g.incluir_cotizable_afp ?? false);
    const inclIncSFS = hasEmpIncInc ? Boolean(individual.incluir_incentivos) : (g.incluir_cotizable_sfs ?? false);

    // ── Bases cotizables per-rate ──
    const baseAFP = salarioBase + comisiones + (inclIncAFP ? incentivos : 0);
    const baseSFS = salarioBase + comisiones + (inclIncSFS ? incentivos : 0);
    const baseSRL = salarioBase + comisiones; // SRL nunca incluye incentivos
    const baseINF = salarioBase + comisiones; // INFOTEP sin incentivos por defecto

    // ── Topes salariales (valores mensuales) ──
    const cotAFP = Math.min(baseAFP, g.tope_afp ?? 464460);
    const cotSFS = Math.min(baseSFS, g.tope_sfs ?? 232230);
    const cotSRL = Math.min(baseSRL, g.tope_srl ?? 92892);

    // ── Montos brutos del empleado (matemáticos) ──
    const afpBruto = cotAFP * (g.porcentaje_afp_empleado || 0.0287);
    const sfsBruto = cotSFS * (g.porcentaje_sfs_empleado || 0.0304);

    // ── Montos netos del empleado (lo que se descuenta efectivamente) ──
    const afpEmp = deductAFP ? afpBruto : 0;
    const sfsEmp = deductSFS ? sfsBruto : 0;
    const totEmp = afpEmp + sfsEmp;

    // ── Monto que el empleador absorbe ──
    const totalAbsorbido = (deductAFP ? 0 : afpBruto) + (deductSFS ? 0 : sfsBruto);

    // ── Empleador (AFP + SFS + SRL + INFOTEP) ──
    const afpEmpl  = cotAFP * (g.porcentaje_afp_empleador || 0.0710);
    const sfsEmpl  = cotSFS * (g.porcentaje_sfs_empleador || 0.0709);
    const srlEmpl  = cotSRL * (g.porcentaje_srl_empleador || 0.0120);
    const infotepV = baseINF * (g.porcentaje_infotep || 0.0100); // INFOTEP sin tope
    const totEmpl  = afpEmpl + sfsEmpl + srlEmpl + infotepV;

    // ── Cápita ──
    const capitaDependientes = individual ? (individual.capita_dependientes || 0) : 0;
    const descontarCapita    = individual ? (individual.descontar_capita ?? true) : (g.descontar_capita_global ?? true);
    const capitaDescontada   = (descontarCapita && capitaDependientes > 0) ? capitaDependientes : 0;

    return {
      salarioCotizable: parseFloat(baseAFP.toFixed(2)),

      afpEmpleado:      parseFloat(afpEmp.toFixed(2)),
      afpEmpleadoBruto: parseFloat(afpBruto.toFixed(2)),
      sfsEmpleado:      parseFloat(sfsEmp.toFixed(2)),
      sfsEmpleadoBruto: parseFloat(sfsBruto.toFixed(2)),
      srlEmpleado:      0,
      totalEmpleado:    parseFloat(totEmp.toFixed(2)),
      totalAbsorbido:   parseFloat(totalAbsorbido.toFixed(2)),

      afpEmpleador:   parseFloat(afpEmpl.toFixed(2)),
      sfsEmpleador:   parseFloat(sfsEmpl.toFixed(2)),
      srlEmpleador:   parseFloat(srlEmpl.toFixed(2)),
      infotep:        parseFloat(infotepV.toFixed(2)),
      totalEmpleador: parseFloat(totEmpl.toFixed(2)),

      totalSeguroSocial: parseFloat((afpBruto + sfsBruto + totEmpl).toFixed(2)),

      descontarAlEmpleado: deductAFP && deductSFS,
      deductAFP,
      deductSFS,
      descuentoNomina:     parseFloat(totEmp.toFixed(2)),
      costoTotalEmpleador: parseFloat((totEmpl + totalAbsorbido).toFixed(2)),

      capitaDependientes,
      capitaDescontada,

      configAplicada: {
        incluirIncentivos:   inclIncAFP || inclIncSFS,
        capitaDependientes,
        descontarAlEmpleado: deductAFP && deductSFS,
        descontarCapita
      }
    };
  }, [globalConfig, configs]);

  return {
    globalConfig,
    configs,
    loading,
    getConfig,
    getEffectiveConfig,
    saveConfig,
    saveGlobalConfig,
    calculateSS,
    reload: loadConfigs
  };
}

export default useSocialSecurityConfig;
